/**
 * Read-only helpers for Chart.js GHG inventory results (no fetchGHGInventoryData import).
 */
import type { GHGInventoryResult } from '../components/charts/ghgInventoryChartTypes';
import { ghgElectricityNonresidentialCategoryLabel } from '../domains/eia861UtilityElectricity/municipalUtilityElectricity';

/** Chart dataset labels written by fetchGHGInventoryData. */
export const LIVE_CHART_SECTOR_LABELS = {
  LDV: 'Light Duty Vehicles',
  HDV: 'Heavy Duty Vehicles',
  ELEC_RES: 'Electricity Residential',
  ELEC_COM: 'Electricity Commercial',
  NG_RES: 'Natural Gas Residential',
  NG_COM: 'Natural Gas Commercial',
  WASTE: 'Waste & wastewater',
  OFFROAD: 'Offroad',
  AGRICULTURE: 'Agriculture',
} as const;

export type LiveChartSectorLabel = (typeof LIVE_CHART_SECTOR_LABELS)[keyof typeof LIVE_CHART_SECTOR_LABELS];

/** Numeric tolerance for published-total vs sector-stack reconciliation (MT CO₂e). */
export const PUBLISHED_TOTAL_RECONCILIATION_TOLERANCE_MT = 0.5;

export type LiveInventorySectorCode =
  | 'ldv'
  | 'hdv'
  | 'elec_res'
  | 'elec_com'
  | 'ng_res'
  | 'ng_com'
  | 'waste'
  | 'offroad'
  | 'agriculture';

const SECTOR_CODE_TO_CANONICAL_LABEL: Record<LiveInventorySectorCode, LiveChartSectorLabel> = {
  ldv: LIVE_CHART_SECTOR_LABELS.LDV,
  hdv: LIVE_CHART_SECTOR_LABELS.HDV,
  elec_res: LIVE_CHART_SECTOR_LABELS.ELEC_RES,
  elec_com: LIVE_CHART_SECTOR_LABELS.ELEC_COM,
  ng_res: LIVE_CHART_SECTOR_LABELS.NG_RES,
  ng_com: LIVE_CHART_SECTOR_LABELS.NG_COM,
  waste: LIVE_CHART_SECTOR_LABELS.WASTE,
  offroad: LIVE_CHART_SECTOR_LABELS.OFFROAD,
  agriculture: LIVE_CHART_SECTOR_LABELS.AGRICULTURE,
};

export const LIVE_INVENTORY_PUBLISHED_SECTOR_CODES: LiveInventorySectorCode[] = [
  'ldv',
  'hdv',
  'elec_res',
  'elec_com',
  'ng_res',
  'ng_com',
  'waste',
  'offroad',
  'agriculture',
];

export function isWasteInventoryLabel(label: string | null | undefined): boolean {
  return label === LIVE_CHART_SECTOR_LABELS.WASTE || label === 'Waste';
}

export interface GhgInventoryChartDataset {
  label?: string;
  data?: Array<number | null | undefined>;
}

export interface ExtractedYearPoint {
  year: number;
  totalMt: number;
  byLabel: Record<string, number>;
}

function chartYears(result: GHGInventoryResult): number[] {
  const labels = (result.chartData?.labels ?? []) as Array<number | string>;
  return labels.map((y) => Number(y)).filter((y) => Number.isFinite(y));
}

function datasets(result: GHGInventoryResult): GhgInventoryChartDataset[] {
  return (result.chartData?.datasets ?? []) as GhgInventoryChartDataset[];
}

/** Resolve the stacked-bar label actually present in chartData (municipal nonres vs commercial). */
export function resolveElectricityNonresChartLabel(
  result: GHGInventoryResult,
  geofips: number | null | undefined
): string {
  const preferred = ghgElectricityNonresidentialCategoryLabel(geofips);
  const labels = datasets(result).map((d) => d.label ?? '');
  if (labels.includes(preferred)) return preferred;
  if (labels.includes(LIVE_CHART_SECTOR_LABELS.ELEC_COM)) return LIVE_CHART_SECTOR_LABELS.ELEC_COM;
  return preferred;
}

export function resolveChartLabelForSectorCode(
  result: GHGInventoryResult,
  sectorCode: LiveInventorySectorCode,
  geofips?: number | null
): string {
  if (sectorCode === 'elec_com') {
    return resolveElectricityNonresChartLabel(result, geofips);
  }
  return SECTOR_CODE_TO_CANONICAL_LABEL[sectorCode];
}

export function chartDatasetLabels(result: GHGInventoryResult): string[] {
  return datasets(result).map((d) => String(d.label ?? '')).filter(Boolean);
}

export function extractSectorValue(
  result: GHGInventoryResult,
  chartLabel: string,
  year: number
): number | null {
  const years = chartYears(result);
  const idx = years.indexOf(year);
  if (idx < 0) return null;
  const ds = datasets(result).find((d) => d.label === chartLabel);
  if (!ds || !ds.data || idx >= ds.data.length) return null;
  const n = Number(ds.data[idx]);
  return Number.isFinite(n) ? n : null;
}

/** Sum of all stacked chart datasets for the year (published inventory total contract). */
export function extractYearTotal(result: GHGInventoryResult, year: number): number | null {
  const years = chartYears(result);
  const idx = years.indexOf(year);
  if (idx < 0) return null;
  let sum = 0;
  let any = false;
  for (const ds of datasets(result)) {
    const n = Number(ds.data?.[idx]);
    if (Number.isFinite(n)) {
      sum += n;
      any = true;
    }
  }
  return any ? sum : null;
}

export function extractSectorValueByCode(
  result: GHGInventoryResult,
  sectorCode: LiveInventorySectorCode,
  year: number,
  geofips?: number | null
): number | null {
  const label = resolveChartLabelForSectorCode(result, sectorCode, geofips);
  return extractSectorValue(result, label, year);
}

export function sumPublishedSectorEmissions(
  result: GHGInventoryResult,
  year: number,
  geofips?: number | null
): number {
  let sum = 0;
  for (const code of LIVE_INVENTORY_PUBLISHED_SECTOR_CODES) {
    const v = extractSectorValueByCode(result, code, year, geofips);
    if (v != null && Number.isFinite(v)) sum += v;
  }
  return sum;
}

export interface PublishedTotalReconciliation {
  year: number;
  publishedTotalMt: number | null;
  sectorSumMt: number;
  deltaMt: number | null;
  withinTolerance: boolean;
  sectorByCode: Partial<Record<LiveInventorySectorCode, number | null>>;
  chartLabelsUsed: Partial<Record<LiveInventorySectorCode, string>>;
  missingChartLabels: string[];
}

export function reconcilePublishedTotalForYear(
  result: GHGInventoryResult,
  year: number,
  geofips?: number | null,
  toleranceMt = PUBLISHED_TOTAL_RECONCILIATION_TOLERANCE_MT
): PublishedTotalReconciliation {
  const publishedTotalMt = extractYearTotal(result, year);
  const sectorByCode: Partial<Record<LiveInventorySectorCode, number | null>> = {};
  const chartLabelsUsed: Partial<Record<LiveInventorySectorCode, string>> = {};
  const presentLabels = new Set(chartDatasetLabels(result));
  const missingChartLabels: string[] = [];

  for (const code of LIVE_INVENTORY_PUBLISHED_SECTOR_CODES) {
    const label = resolveChartLabelForSectorCode(result, code, geofips);
    chartLabelsUsed[code] = label;
    if (!presentLabels.has(label)) missingChartLabels.push(label);
    sectorByCode[code] = extractSectorValue(result, label, year);
  }

  const sectorSumMt = sumPublishedSectorEmissions(result, year, geofips);
  const deltaMt =
    publishedTotalMt != null ? sectorSumMt - publishedTotalMt : null;
  const withinTolerance =
    deltaMt != null && Math.abs(deltaMt) <= toleranceMt;

  return {
    year,
    publishedTotalMt,
    sectorSumMt,
    deltaMt,
    withinTolerance,
    sectorByCode,
    chartLabelsUsed,
    missingChartLabels,
  };
}

export function extractHistoricalTotals(result: GHGInventoryResult): ExtractedYearPoint[] {
  return chartYears(result).map((year) => {
    const byLabel: Record<string, number> = {};
    for (const ds of datasets(result)) {
      const label = ds.label ?? '';
      const n = extractSectorValue(result, label, year);
      if (n != null) byLabel[label] = n;
    }
    return {
      year,
      totalMt: extractYearTotal(result, year) ?? 0,
      byLabel,
    };
  });
}

export function inventoryYearFromResult(result: GHGInventoryResult, preferredYear = 2023): number {
  const years = chartYears(result);
  if (years.includes(preferredYear)) return preferredYear;
  if (result.latestYear != null && years.includes(result.latestYear)) return result.latestYear;
  return years.length ? years[years.length - 1] : preferredYear;
}
